import { User } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';

export interface LessonProgress {
  lessonId: string;
  watched: boolean;
  liked: boolean | null;
}

// Cache structure
interface ProgressCache {
  [userId: string]: {
    [lessonId: string]: LessonProgress;
  };
}

// In-memory cache
const progressCache: ProgressCache = {};

// Get current user from Supabase
const getCurrentUser = async (): Promise<User> => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) {
    throw new Error('Você precisa estar logado para acessar este conteúdo');
  }
  return user;
};

// Clear cache for user
const clearUserCache = (userId: string) => {
  delete progressCache[userId];
};

export const lessonProgressService = {
  // Load progress for multiple lessons at once
  async loadProgress(lessonIds: string[]): Promise<Record<string, LessonProgress>> {
    try {
      const user = await getCurrentUser();
      
      // Check cache first
      if (progressCache[user.id]) {
        const cachedResults: Record<string, LessonProgress> = {};
        let needsFetch = false;
        
        lessonIds.forEach(id => {
          if (progressCache[user.id][id]) {
            cachedResults[id] = progressCache[user.id][id];
          } else {
            needsFetch = true;
          }
        });
        
        if (!needsFetch) {
          return cachedResults;
        }
      }

      // Fetch from database
      const { data, error } = await supabase
        .from('lesson_progress')
        .select('lesson_id, watched, liked')
        .eq('user_id', user.id)
        .in('lesson_id', lessonIds);

      if (error) {
        console.error('Erro ao carregar progresso:', error);
        return {};
      }

      // Initialize cache for user if needed
      if (!progressCache[user.id]) {
        progressCache[user.id] = {};
      }

      // Transform and cache results
      const results: Record<string, LessonProgress> = {};
      lessonIds.forEach(id => {
        const progress = data?.find(p => p.lesson_id === id) || { lesson_id: id, watched: false, liked: null };
        const formatted: LessonProgress = {
          lessonId: id,
          watched: progress.watched || false,
          liked: progress.liked
        };
        results[id] = formatted;
        progressCache[user.id][id] = formatted;
      });

      return results;
    } catch (error) {
      console.error('Erro ao carregar progresso:', error);
      return {};
    }
  },

  // Update progress for a single lesson
  async updateProgress(lessonId: string, updates: { watched?: boolean; liked?: boolean | null }): Promise<void> {
    try {
      console.log('Atualizando progresso:', { lessonId, updates });
      const user = await getCurrentUser();

      // Check if record exists
      // Get module and course IDs from lesson registry
      const lessonData = await import(`../pages/Courses/registry`);
      const moduleInfo = lessonData.modules.find(m => 
        m.courses.some(c => 
          c.lessons.some(l => l.itemId === lessonId)
        )
      );
      const courseInfo = moduleInfo?.courses.find(c => 
        c.lessons.some(l => l.itemId === lessonId)
      );

      // Try to insert first (upsert)
      const { error: upsertError } = await supabase
        .from('lesson_progress')
        .upsert({
          user_id: user.id,
          lesson_id: lessonId,
          module_id: moduleInfo?.name,
          course_id: courseInfo?.courseTitle,
          last_viewed_at: updates.watched ? new Date().toISOString() : undefined,
          ...updates,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,lesson_id'
        });

      if (upsertError) {
        console.error('Erro na operação:', upsertError);
        throw new Error('Falha ao salvar progresso');
      }
      
      console.log('Progresso salvo com sucesso:', { lessonId, updates });

      // Update cache
      if (!progressCache[user.id]) {
        progressCache[user.id] = {};
      }

      progressCache[user.id][lessonId] = {
        lessonId,
        watched: updates.watched ?? progressCache[user.id][lessonId]?.watched ?? false,
        liked: updates.liked ?? progressCache[user.id][lessonId]?.liked ?? null
      };

    } catch (error) {
      console.error('Erro ao atualizar progresso:', error);
      throw error;
    }
  },

  // Clear cache (useful when logging out)
  clearCache() {
    Object.keys(progressCache).forEach(clearUserCache);
  }
};
